import { Card, Input, Popover, Tooltip, Typography } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

const { Text } = Typography;
const { Search } = Input;

const formatCurrency = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const SALES_COLOR = "#0891b2";
const PROFIT_COLOR = "#16a34a";
const BAR_COLOR = SALES_COLOR;
const MIN_BAR_WIDTH_PCT = 4;

const TopCustomers = () => {
  const raw = useSelector((state) => state.dashboard.list?.customerSaleProfit) || [];
  const [searchText, setSearchText] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);

  const customers = useMemo(() => {
    const byLabel = {};
    raw.forEach((item) => {
      if (!item || !item.label) return;
      if (!byLabel[item.label]) {
        byLabel[item.label] = { label: item.label, sales: 0, profit: 0 };
      }
      if (item.type === "Sales") byLabel[item.label].sales = Number(item.value) || 0;
      if (item.type === "Profit") byLabel[item.label].profit = Number(item.value) || 0;
    });
    return Object.values(byLabel)
      .filter((c) => c.sales > 0)
      .sort((a, b) => b.sales - a.sales);
  }, [raw]);

  const filteredCustomers = useMemo(() => {
    if (!searchText.trim()) return customers;
    const q = searchText.trim().toLowerCase();
    return customers.filter((c) => c.label.toLowerCase().includes(q));
  }, [customers, searchText]);

  const maxSales = useMemo(() => Math.max(...filteredCustomers.map((c) => c.sales), 1), [filteredCustomers]);
  const maxProfit = useMemo(() => Math.max(...filteredCustomers.map((c) => c.profit), 1), [filteredCustomers]);
  const maxValue = Math.max(maxSales, maxProfit, 1);

  const barStyle = (widthPct, color) => ({
    width: `${Math.max(widthPct, widthPct > 0 ? MIN_BAR_WIDTH_PCT : 0)}%`,
    height: "100%",
    borderRadius: 4,
    backgroundColor: color,
    minWidth: widthPct > 0 ? 6 : 0,
    transition: "width 0.3s ease",
  });

  return (
    <Card
      title="Top Customers"
      size="small"
      className="dashboard-chart-card dashboard-chart-card-compact top-customers-card"
      extra={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>by revenue</Text>
          <Popover
            content={
              <div style={{ width: 220 }}>
                <Search
                  placeholder="Search customer..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(v) => setSearchText(v || "")}
                  size="small"
                  style={{ marginBottom: 0 }}
                />
              </div>
            }
            title="Filter by name"
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          >
            <Tooltip title="Filter / Search">
              <span
                style={{
                  cursor: "pointer",
                  padding: "2px 4px",
                  borderRadius: 4,
                  color: searchText ? "#1890ff" : "rgba(0,0,0,0.45)",
                }}
              >
                <FilterOutlined />
              </span>
            </Tooltip>
          </Popover>
        </span>
      }
    >
      <div className="top-customers-wrap">
        {filteredCustomers.length === 0 ? (
          <Text type="secondary">
            {customers.length === 0
              ? "No customer data in selected period"
              : "No customers match your search"}
          </Text>
        ) : (
          <>
            <div className="top-customers-list">
              {filteredCustomers.map((c) => {
                const salesPct = maxValue > 0 ? (c.sales / maxValue) * 100 : 0;
                const profitPct = maxValue > 0 ? (c.profit / maxValue) * 100 : 0;
                return (
                  <div key={c.label} style={{ minHeight: 40 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <Text ellipsis style={{ fontSize: 13, flex: "1 1 0", minWidth: 0, marginRight: 8 }}>
                        {c.label}
                      </Text>
                      <Text strong style={{ fontSize: 13, flexShrink: 0 }}>{formatCurrency(c.sales)}</Text>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Tooltip title={`Sales: ${formatCurrency(c.sales)}`} placement="top">
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#f0f0f0",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                        >
                          <div style={barStyle(salesPct, SALES_COLOR)} />
                        </div>
                      </Tooltip>
                      <Tooltip title={`Profit: ${formatCurrency(c.profit)}`} placement="top">
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#f0f0f0",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                        >
                          <div style={barStyle(profitPct, PROFIT_COLOR)} />
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredCustomers.length > 0 && (
              <div className="top-customers-legend">
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: SALES_COLOR }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>Sales</Text>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: PROFIT_COLOR }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>Profit</Text>
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default TopCustomers;
